import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styles from './styles/ClassroomViewer.module.css';
import ClassroomCanvasOptions from './ClassroomCanvasOptions';
import CanvasContext from '../../../context/CanvasContext';
import ClassroomCanvas from './ClassroomCanvas';
import socket, { addJoinEvent, removeJoinEvent } from './../../../socket';
import ClassroomCanvasSidebar from './ClassroomCanvasSidebar';
import buildPositions from './buildPositions';

const ClassroomCanvasViewer = () => {
    const { assignmentId } = useParams();
    const { fetchAssignmentSubmissions } = useContext(CanvasContext);

    const [activeStudents, setActiveStudents] = useState([]);
    const [studentPositions, setStudentPositions] = useState({});
    const [lockedUsers, setLockedUsers] = useState([]);
    const [universalLock, setUniversalLock] = useState(false);

    useEffect(() => {
        if (assignmentId) {
            fetchAssignmentSubmissions();
        }
    }, [assignmentId]);

    // joinAsTeacher acks one batched snapshot (roster + lock state). The join
    // registry replays it on every reconnect, so a teacher refresh or a server
    // cold start rebuilds the whole view without any manual steps.
    const handleSnapshot = useCallback((err, response) => {
        if (err || response?.status !== 'OK') {
            if (err) console.warn('joinAsTeacher not acknowledged; will retry on reconnect.', err);
            return;
        }
        setActiveStudents(response.students);
        setStudentPositions(buildPositions(response.students));
        setUniversalLock(Boolean(response.locks?.universal));
        setLockedUsers(response.locks?.lockedUserIds || []);
    }, []);

    useEffect(() => {
        if (!assignmentId) {
            return;
        }

        addJoinEvent('joinAsTeacher', { assignmentId }, handleSnapshot);

        const handleActiveUpdate = (data) => {
            setActiveStudents(data.newValues);
        };

        const handleSubmissionUpdate = () => {
            fetchAssignmentSubmissions();
        };

        const handleStartPageUpdate = (data) => {
            if (!data?.situationId) return;
            setActiveStudents(prevStudents =>
                prevStudents.map(student =>
                    student.userId === data.studentId
                        ? { ...student, situationId: data.situationId, startStatus: false }
                        : student
                )
            );
        };

        // Surgical position update: only the node the student left and the node
        // they entered get new array identities, so memoized canvas nodes for
        // every other position skip re-rendering.
        const handleUpdatePosition = (data) => {
            const { studentId, studentName, situationId } = data || {};
            if (!studentId || !situationId) {
                return;
            }
            setStudentPositions((prev) => {
                const currentNodeId = Object.keys(prev).find((nId) =>
                    prev[nId].some((s) => s.studentId === studentId)
                );
                if (currentNodeId === situationId) {
                    return prev;
                }
                const updated = { ...prev };
                if (currentNodeId) {
                    const remaining = updated[currentNodeId].filter((s) => s.studentId !== studentId);
                    if (remaining.length > 0) {
                        updated[currentNodeId] = remaining;
                    } else {
                        delete updated[currentNodeId];
                    }
                }
                updated[situationId] = [...(updated[situationId] || []), { studentId, studentName }];
                return updated;
            });
        };

        const handleStudentLeft = ({ userId }) => {
            setStudentPositions((prev) => {
                const nodeId = Object.keys(prev).find((nId) =>
                    prev[nId].some((s) => s.studentId === userId)
                );
                if (!nodeId) return prev;
                const updated = { ...prev };
                const remaining = updated[nodeId].filter((s) => s.studentId !== userId);
                if (remaining.length > 0) {
                    updated[nodeId] = remaining;
                } else {
                    delete updated[nodeId];
                }
                return updated;
            });
        };

        // Server-authoritative lock state; also keeps multiple teacher tabs in sync.
        const handleLocksUpdated = ({ universal, lockedUserIds }) => {
            setUniversalLock(Boolean(universal));
            setLockedUsers(lockedUserIds || []);
        };

        socket.on('activeStudentsUpdate', handleActiveUpdate);
        socket.on('submissionUpdated', handleSubmissionUpdate);
        socket.on('startPageUpdate', handleStartPageUpdate);
        socket.on('updatePosition', handleUpdatePosition);
        socket.on('studentLeft', handleStudentLeft);
        socket.on('locksUpdated', handleLocksUpdated);

        return () => {
            removeJoinEvent('joinAsTeacher');
            socket.off('activeStudentsUpdate', handleActiveUpdate);
            socket.off('submissionUpdated', handleSubmissionUpdate);
            socket.off('startPageUpdate', handleStartPageUpdate);
            socket.off('updatePosition', handleUpdatePosition);
            socket.off('studentLeft', handleStudentLeft);
            socket.off('locksUpdated', handleLocksUpdated);
        };
    }, [assignmentId, fetchAssignmentSubmissions, handleSnapshot]);

    // Locks live in Redis server-side; this explicit release covers deliberate
    // navigation away, and the server's teacher-disconnect grace timer covers
    // crashes and closed tabs where beforeunload never fires.
    useEffect(() => {
        if (!assignmentId) return;
        const handleBeforeUnload = () => {
            socket.emit('releaseEveryone', { assignmentId });
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            socket.emit('releaseEveryone', { assignmentId });
        };
    }, [assignmentId]);

    const [selectedUser, setSelectedUser] = useState(null);

    const handleSelectedUser = useCallback((student) => {
        setSelectedUser(student);
    }, []);

    return (
        <div className={styles.container}>
            {selectedUser &&
                <div className={styles.createNodeSidebar}>
                    <ClassroomCanvasOptions userInfo = {selectedUser}/>
                </div>
            }
            <div className={styles.canvas}>
                <ClassroomCanvas assignmentId={assignmentId} studentPositions={studentPositions} universalLock={universalLock} setUniversalLock={setUniversalLock} selectedUserPropFunction={handleSelectedUser}/>
            </div>
            <div className={styles.sidebar}>
                <ClassroomCanvasSidebar assignmentId={assignmentId} activeStudents={activeStudents} selectedUser={selectedUser} selectedUserPropFunction = {handleSelectedUser} lockedUsers={lockedUsers} setLockedUsers={setLockedUsers}/>
            </div>
        </div>
    );
};

export default ClassroomCanvasViewer;
